import { API_BASE_URL, createAuthHeader } from '../../../config/api.config';
import axios from 'axios';
import { formatLocalDate } from '../../../utils/dateUtils';
import { getApiErrorMessage } from '../../../utils/apiError';

/**
 * Fetch filter options for the Experiment Management page
 * Returns hierarchical structure like Analysis.jsx for cascading filters
 */
export const fetchFilterOptions = async (customerId) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(
      `${API_BASE_URL}/experiment-management-filters?clientId=${customerId}`,
      { 
        headers: {
          ...createAuthHeader(token),
        }
      }
    );

    // Return the hierarchical structure directly for cascading filters
    return { groups: response.data.groups };
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch experiments based on filters
 * Calls the real experiments-management endpoint
 */
export const fetchExperiments = async (filters, customerId) => {
  try {
    const token = localStorage.getItem('access_token');
    
    // Build query parameters
    const params = {
      clientId: customerId,
      startDate: formatDateForAPI(filters.dateRange.start),
      endDate: formatDateForAPI(filters.dateRange.end)
    };
    
    // Add optional filters if they exist
    if (filters.group) params.group = filters.group;
    if (filters.location) params.location = filters.location;
    if (filters.machine) params.machine = filters.machine;
    if (filters.route) params.route = filters.route;
    
    // Build query string
    const queryString = new URLSearchParams(params).toString();
    
    const response = await axios.get(
      `${API_BASE_URL}/experiments-management?${queryString}`,
      { headers: createAuthHeader(token) }
    );
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Format date for API (YYYY-MM-DD format)
 */
const formatDateForAPI = (date) => {
  if (!date) return null;
  return formatLocalDate(date);
};

/**
 * Fetch available routes for experiment management filters
 */
export const fetchRouteOptions = async (filters, customerId) => {
  try {
    const token = localStorage.getItem('access_token');
    const params = new URLSearchParams();
    params.append('clientId', customerId);

    const startDate = formatDateForAPI(filters?.dateRange?.start);
    const endDate = formatDateForAPI(filters?.dateRange?.end);

    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    if (filters?.group) params.append('group', filters.group);
    if (filters?.location) params.append('location', filters.location);
    if (filters?.machine) params.append('machine', filters.machine);

    const response = await axios.get(
      `${API_BASE_URL}/experiment-management/routes?${params.toString()}`,
      { headers: createAuthHeader(token) }
    );

    return response.data.routes || [];
  } catch (error) {
    throw error;
  }
};

/**
 * Accept an experiment
 */
export const acceptExperiment = async (experimentId, clientId, pricingOverride) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.post(
      `${API_BASE_URL}/experiments/${experimentId}/status?clientId=${clientId}`,
      { status: 'accepted', pricingOverride },
      { headers: createAuthHeader(token) }
    );
    
    return {
      success: true,
      message: 'Experiment accepted',
      experimentId,
      newStatus: 'accepted',
      experiment: response.data
    };
  } catch (error) {
    return { success: false, message: getApiErrorMessage(error, 'Failed to accept experiment') };
  }
};

/**
 * Reject an experiment
 */
export const rejectExperiment = async (experimentId, reason = '', clientId) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.post(
      `${API_BASE_URL}/experiments/${experimentId}/status?clientId=${clientId}`,
      { status: 'rejected', feedback: reason },
      { headers: createAuthHeader(token) }
    );
    
    return {
      success: true,
      message: `Experiment rejected: ${reason}`,
      experimentId,
      newStatus: 'rejected',
      rejectionReason: reason
    };
  } catch (error) {
    return { success: false, message: getApiErrorMessage(error, 'Failed to reject experiment') };
  }
};

/**
 * Begin an experiment (change status to running)
 */
export const beginExperiment = async (experimentId, clientId) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.post(
      `${API_BASE_URL}/experiments/${experimentId}/status?clientId=${clientId}`,
      { status: 'running' },
      { headers: createAuthHeader(token) }
    );
    
    return {
      success: true,
      message: 'Experiment started',
      experimentId,
      newStatus: 'running',
      experiment: response.data
    };
  } catch (error) {
    return { success: false, message: getApiErrorMessage(error, 'Failed to start experiment') };
  }
};

/**
 * Update experiment status to any valid status
 */
export const updateExperimentStatus = async (experimentId, status, clientId, reason = '', pricingOverride) => {
  try {
    const token = localStorage.getItem('access_token');
    const payload = { status };
    if (pricingOverride) payload.pricingOverride = pricingOverride;

    // Add feedback/reason if provided (for rejections)
    if (reason) {
      payload.feedback = reason;
    }

    const response = await axios.post(
      `${API_BASE_URL}/experiments/${experimentId}/status?clientId=${clientId}`,
      payload,
      { headers: createAuthHeader(token) }
    );

    return {
      success: true,
      message: `Experiment moved to ${status}`,
      experimentId,
      newStatus: status,
      experiment: response.data
    };
  } catch (error) {
    return {
      success: false,
      message: getApiErrorMessage(error, `Failed to update experiment to ${status}`),
      error: error.message
    };
  }
};

/**
 * Fetch AI-powered candidate suggestions for creating experiments
 * @param {string} clientId - Client ID
 * @param {number} storeId - Store/Machine ID (d_store_id)
 * @param {number} productId - Product ID (d_product_id)
 * @param {string} experimentType - 'promo_precio_unitario' for discount, 'reemplazo_producto' for replacement
 * @returns {Promise<{candidates?: Array, suggestedDiscount?: number, suggestion?: string}>}
 */
export const fetchCandidateSuggestion = async (clientId, storeId, productId, experimentType) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(
      `${API_BASE_URL}/experiments/candidate-suggestion`,
      {
        params: { clientId, storeId, productId, experimentType },
        headers: createAuthHeader(token)
      }
    );
    return response.data;
  } catch (error) {
    // Return empty result on error (silent fail - just don't show AI suggestions)
    return { candidates: [] };
  }
};

/**
 * Replacement candidates for one product in several machines at once (max 50).
 * @returns {Promise<{suggestions: Object, volumeSuggestions: Object, quadrants: Object}>} each keyed by
 *   storeId: candidates by margin and by volume (same shape as fetchCandidateSuggestion) and the quadrant
 */
export const fetchCandidateSuggestionBatch = async (clientId, productId, storeIds) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(
      `${API_BASE_URL}/experiments/candidate-suggestion/batch`,
      {
        params: { clientId, productId, storeIds: storeIds.join(',') },
        headers: createAuthHeader(token)
      }
    );
    return {
      suggestions: response.data?.suggestions || {},
      volumeSuggestions: response.data?.volumeSuggestions || {},
      quadrants: response.data?.quadrants || {},
    };
  } catch (error) {
    // Silent fail, like the single call: the rows just show no suggestion
    return { suggestions: {}, volumeSuggestions: {}, quadrants: {} };
  }
};

/**
 * A replacement experiment's alternatives by margin and by volume, and the machine's quadrant.
 * @returns {Promise<{quadrant: string|null, margin: Array, volume: Array}>}
 */
export const fetchReplacementAlternatives = async (clientId, experimentId) => {
  try {
    const token = localStorage.getItem('access_token');
    const response = await axios.get(
      `${API_BASE_URL}/experiments/${experimentId}/alternatives`,
      { params: { clientId }, headers: createAuthHeader(token) }
    );
    return response.data;
  } catch (error) {
    // Silent fail: the choice falls back to what the screen already offers
    return { quadrant: null, margin: [], volume: [] };
  }
};
